// Org-admin lookup tables tab — manage category-scoped reference data (contract types,
// arrangement types, business units, procurement methods, meeting types) with add,
// edit, and toggle.

import { useState } from "react";
import { Plus, Pencil } from "lucide-react";
import { Button, LoadingSpinner, Alert } from "../common";
import {
  useLookupItems,
  useCreateLookupItem,
  useUpdateLookupItem,
} from "../../hooks/useLookups";
import type { LookupCategory, LookupItem } from "../../types";

const CATEGORIES: { id: LookupCategory; label: string }[] = [
  { id: "contract-type", label: "Contract Type" },
  { id: "arrangement-type", label: "Arrangement Type" },
  { id: "business-unit", label: "Business Unit" },
  { id: "procurement-method", label: "Procurement Method" },
  { id: "meeting-type", label: "Meeting Type" },
];

export default function LookupTablesTab() {
  const [selectedCategory, setSelectedCategory] =
    useState<LookupCategory>("contract-type");
  const [showAddForm, setShowAddForm] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newSortOrder, setNewSortOrder] = useState(0);
  const [editingItem, setEditingItem] = useState<LookupItem | null>(null);
  const [editLabel, setEditLabel] = useState("");
  const [editSortOrder, setEditSortOrder] = useState(0);

  const { data, isLoading, error } = useLookupItems(selectedCategory);
  const createMutation = useCreateLookupItem();
  const updateMutation = useUpdateLookupItem();

  const items = data?.items ?? [];

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    await createMutation.mutateAsync({
      category: selectedCategory,
      label: newLabel,
      sortOrder: newSortOrder,
    });
    setNewLabel("");
    setNewSortOrder(items.length);
    setShowAddForm(false);
  };

  const handleToggleActive = async (item: LookupItem) => {
    await updateMutation.mutateAsync({
      itemId: item.itemId,
      input: { isActive: !item.isActive },
    });
  };

  const startEditing = (item: LookupItem) => {
    setEditingItem(item);
    setEditLabel(item.label);
    setEditSortOrder(item.sortOrder);
    setShowAddForm(false);
  };

  const handleEditSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingItem) return;
    await updateMutation.mutateAsync({
      itemId: editingItem.itemId,
      input: { label: editLabel, sortOrder: editSortOrder },
    });
    setEditingItem(null);
  };

  return (
    <div className="flex gap-6">
      {/* Left panel: category list */}
      <div className="w-56 shrink-0">
        <h3 className="text-h4 text-slate-900 dark:text-slate-50 mb-3">
          Categories
        </h3>
        <nav className="space-y-1">
          {CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`w-full text-left px-3 py-2 rounded-md text-body-sm transition-colors ${
                selectedCategory === cat.id
                  ? "bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300 font-medium"
                  : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
              }`}
            >
              {cat.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Right panel: items */}
      <div className="flex-1">
        <div className="flex items-center justify-end mb-4">
          <Button
            size="sm"
            icon={<Plus className="h-4 w-4" />}
            onClick={() => {
              setNewSortOrder(items.length);
              setShowAddForm(true);
            }}
          >
            Add
          </Button>
        </div>

        {error && <Alert variant="error">{(error as Error).message}</Alert>}

        {isLoading && (
          <div className="flex justify-center py-8">
            <LoadingSpinner size="md" />
          </div>
        )}

        {!isLoading && items.length === 0 && (
          <p className="text-body-sm text-slate-500 dark:text-slate-400 py-8 text-center">
            No items in this category yet.
          </p>
        )}

        {!isLoading && items.length > 0 && (
          <div className="space-y-2">
            {items.map((item) => (
              <div
                key={item.itemId}
                className={`card flex items-center justify-between px-4 py-3 ${
                  !item.isActive ? "opacity-50" : ""
                }`}
                data-inactive={!item.isActive ? "" : undefined}
              >
                <div className="flex items-center gap-3">
                  <span className="text-body text-slate-900 dark:text-slate-50">
                    {item.label}
                  </span>
                  <span className="text-caption text-slate-400">
                    #{item.sortOrder}
                  </span>
                  {!item.isActive && (
                    <span className="badge-archived text-caption">
                      Inactive
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    iconOnly
                    size="sm"
                    icon={<Pencil className="h-4 w-4" />}
                    aria-label="Edit"
                    onClick={() => startEditing(item)}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleToggleActive(item)}
                    aria-label={item.isActive ? "Deactivate" : "Activate"}
                  >
                    {item.isActive ? "Deactivate" : "Activate"}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {editingItem && (
          <form onSubmit={handleEditSave} className="card mt-4 p-4 space-y-3">
            <div>
              <label htmlFor="edit-label" className="label">
                Label
              </label>
              <input
                id="edit-label"
                type="text"
                value={editLabel}
                onChange={(e) => setEditLabel(e.target.value)}
                required
                className="input"
                maxLength={200}
              />
            </div>
            <div>
              <label htmlFor="edit-sort" className="label">
                Sort Order
              </label>
              <input
                id="edit-sort"
                type="number"
                value={editSortOrder}
                onChange={(e) => setEditSortOrder(Number(e.target.value))}
                min={0}
                className="input"
              />
            </div>
            <div className="flex gap-2">
              <Button
                type="submit"
                size="sm"
                disabled={!editLabel.trim()}
                loading={updateMutation.isPending}
              >
                Save
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setEditingItem(null)}
              >
                Cancel
              </Button>
            </div>
          </form>
        )}

        {showAddForm && (
          <form onSubmit={handleAdd} className="card mt-4 p-4 space-y-3">
            <div>
              <label htmlFor="lookup-label" className="label">
                Label
              </label>
              <input
                id="lookup-label"
                type="text"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                required
                className="input"
                maxLength={200}
              />
            </div>
            <div>
              <label htmlFor="lookup-sort" className="label">
                Sort Order
              </label>
              <input
                id="lookup-sort"
                type="number"
                value={newSortOrder}
                onChange={(e) => setNewSortOrder(Number(e.target.value))}
                min={0}
                className="input"
              />
            </div>
            <div className="flex gap-2">
              <Button
                type="submit"
                size="sm"
                disabled={!newLabel.trim()}
                loading={createMutation.isPending}
              >
                Save
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setShowAddForm(false)}
              >
                Cancel
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
